import React from "react";

/**
 * Top-level error boundary. Guarantees that an unhandled render error in any
 * child never produces a blank/black screen in production — users always see
 * a readable error card with a retry button.
 */
export default class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("RootErrorBoundary caught:", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        data-testid="root-error-boundary"
        style={{ background: "var(--background, #0a0f0a)", color: "var(--text-primary, #e5f5e5)" }}
      >
        <div
          className="max-w-xl w-full border p-8 space-y-4"
          style={{
            borderColor: "var(--primary-accent, #a3ff12)",
            background: "rgba(0,0,0,0.4)",
            fontFamily: "var(--font-mono, monospace)",
          }}
        >
          <div
            className="font-bold tracking-widest uppercase text-xs"
            style={{ color: "var(--primary-accent, #a3ff12)" }}
          >
            SIGNAL LOST
          </div>
          <h1 className="text-2xl font-extrabold uppercase">Something went wrong</h1>
          <p className="text-sm opacity-80">
            The browser hit an unexpected error while rendering. This is usually a
            transient issue — reloading the page clears it. If the problem persists,
            please let us know.
          </p>
          <pre
            className="text-xs p-3 overflow-auto max-h-40 border"
            style={{
              borderColor: "rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.5)",
              color: "var(--text-secondary, #99aa99)",
            }}
          >
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex gap-3">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 border font-bold tracking-widest uppercase text-xs"
              style={{ color: "var(--primary-accent, #a3ff12)", borderColor: "var(--primary-accent, #a3ff12)" }}
              data-testid="root-error-reload-btn"
            >
              Reload
            </button>
            <a
              href="/"
              className="px-4 py-2 border font-bold tracking-widest uppercase text-xs"
              style={{ color: "var(--text-secondary, #99aa99)", borderColor: "rgba(255,255,255,0.2)" }}
              data-testid="root-error-home-btn"
            >
              Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
